// Analytics integrations. Tracking IDs are read from Vite env vars
// (set them in project secrets as VITE_* so they are exposed to the client).
//
// Add to your project secrets (Build / Runtime env):
//   VITE_GA4_MEASUREMENT_ID   e.g. G-XXXXXXXXXX
//   VITE_GTM_CONTAINER_ID     e.g. GTM-XXXXXXX
//   VITE_CLARITY_PROJECT_ID   e.g. xxxxxxxxxx
//
// Search Console / Google Business Profile / LinkedIn / YouTube don't need a
// runtime script — they're tracked via verification meta + UTMs.

export const ANALYTICS = {
  ga4: import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined,
  gtm: import.meta.env.VITE_GTM_CONTAINER_ID as string | undefined,
  clarity: import.meta.env.VITE_CLARITY_PROJECT_ID as string | undefined,
};

export type AnalyticsEvent =
  | "consultation_step"
  | "form_start"
  | "consultation_submit"
  | "whatsapp_click"
  | "call_click"
  | "email_click"
  | "linkedin_click"
  | "calendar_click"
  | "article_view"
  | "video_play"
  | "outbound_click";

type EventParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
  }
}

export function trackEvent(name: AnalyticsEvent, params: EventParams = {}) {
  if (typeof window === "undefined") return;
  // GA4 (direct or via GTM dataLayer)
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event: name, ...params });
  if (typeof window.gtag === "function") {
    window.gtag("event", name, params);
  }
  if (typeof window.clarity === "function") {
    try {
      window.clarity("event", name);
    } catch {
      /* noop */
    }
  }
}

/** UTM helper — append campaign params to outbound links. */
export function withUtm(
  url: string,
  utm: { source: string; medium: string; campaign: string; content?: string; term?: string }
) {
  try {
    const u = new URL(url);
    u.searchParams.set("utm_source", utm.source);
    u.searchParams.set("utm_medium", utm.medium);
    u.searchParams.set("utm_campaign", utm.campaign);
    if (utm.content) u.searchParams.set("utm_content", utm.content);
    if (utm.term) u.searchParams.set("utm_term", utm.term);
    return u.toString();
  } catch {
    return url;
  }
}
